import { useEffect, useState } from 'react';
import { BRAND } from '@/brand';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import AdminSidebar from '../components/AdminSidebar';
import AdminProfileMenu from '../components/AdminProfileMenu';
import { RefreshCw, Inbox } from 'lucide-react';

/* Admin views for the marketing intake submissions captured by the Grow & Raise /
   Conncct forms: Partner applications, Expert-network applications, Sprint intakes.
   Reads GET /auth/admin/intake; status via PATCH /auth/admin/intake/:type/:id.

   Brand scoping — the endpoint returns everything, this page shows only what
   belongs to the domain it is being viewed on:
   - /partner runs on BOTH brands and stamps each application `program:
     'corporate' | 'consulting'`, so partner rows are filtered to this brand.
     Rows predating that field have no `program` and fall to consulting, the
     default brand, rather than being hidden from everyone.
   - /sprints and /experts are consulting-only (BrandGate in App.tsx), so those
     two tabs are hidden entirely on corporate — there is nothing to show. */

const IS_CORP = BRAND === 'corporate';
const ACCENT = IS_CORP ? '#F08A24' : '#2563EB';

type Row = Record<string, any> & { id: string; submittedAt?: string; status?: string; program?: string };
type Intake = { partner: Row[]; expert: Row[]; sprint: Row[] };
type TabKey = keyof Intake;

const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: 'partner', label: 'Partner applications' },
  { key: 'expert', label: 'Expert applications' },
  { key: 'sprint', label: 'Sprint intakes' },
];
const TABS = IS_CORP ? ALL_TABS.filter(t => t.key === 'partner') : ALL_TABS;

// A partner application belongs to this brand's admin view.
const ownPartner = (r: Row) => (String(r.program || '').toLowerCase() === 'corporate') === IS_CORP;

const STATUSES = ['new', 'contacted', 'approved', 'dismissed'];
const HIDE = new Set(['id', 'status', 'submittedAt', 'updatedAt', 'type', 'name', 'firm', 'cname', 'email', 'ref', 'program']);

const fmtDate = (d?: string) => { try { return d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; } catch { return d || '—'; } };
const label = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());
const val = (v: any) => Array.isArray(v) ? (v.length ? v.join(', ') : '—') : (v === '' || v == null ? '—' : String(v));

const CSS = `
.adminx{font-family:'Poppins',system-ui,sans-serif;color:#0F172A}
.adminx .ph h1{font-size:22px;font-weight:700;letter-spacing:-.02em}
.adminx .ph p{font-size:13px;color:#64748B;margin-top:2px}
.adminx .tabs{display:flex;gap:6px;margin:20px 0 16px;flex-wrap:wrap}
.adminx .tab{border:1px solid #E6E9ED;background:#fff;border-radius:999px;padding:8px 16px;font-size:13px;font-weight:600;color:#475569;cursor:pointer}
.adminx .tab.on{background:var(--acc);border-color:var(--acc);color:#fff}
.adminx .tab .cnt{margin-left:7px;font-size:11px;font-weight:700;opacity:.8}
.adminx .card{background:#fff;border:1px solid #E6E9ED;border-radius:14px;box-shadow:0 1px 2px rgba(15,23,42,.05);margin-bottom:12px}
.adminx .card-h{display:flex;align-items:flex-start;gap:12px;padding:16px 18px;border-bottom:1px solid #EFF1F4}
.adminx .card-h .av{width:40px;height:40px;border-radius:10px;background:var(--soft);color:var(--acc);display:grid;place-items:center;font-weight:700;flex:none}
.adminx .card-h b{font-size:14.5px;font-weight:600}
.adminx .card-h .em{font-size:12.5px;color:#64748B}
.adminx .pill{margin-left:auto;font-size:12px;font-weight:600;border:1px solid #E6E9ED;border-radius:8px;padding:6px 10px;background:#fff;color:#334155;font-family:inherit}
.adminx .kv{display:grid;grid-template-columns:repeat(2,1fr);gap:0 22px;padding:14px 18px}
.adminx .kv div{padding:7px 0;border-bottom:1px solid #F4F6F8;font-size:12.5px}
.adminx .kv span{color:#94A3B8;font-weight:600;display:block;font-size:10.5px;text-transform:uppercase;letter-spacing:.04em}
.adminx .kv b{font-weight:600;color:#334155;word-break:break-word}
.adminx .foot{display:flex;align-items:center;gap:10px;padding:10px 18px;border-top:1px solid #EFF1F4;font-size:11.5px;color:#94A3B8}
.adminx .empty{background:#fff;border:1px dashed #D7DDE5;border-radius:14px;padding:44px;text-align:center;color:#64748B}
.adminx .btn{display:inline-flex;align-items:center;gap:7px;border:1px solid #E6E9ED;background:#fff;border-radius:9px;padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer;color:#334155}
@media(max-width:640px){.adminx .kv{grid-template-columns:1fr}}
`;

export default function AdminIntake({ embedded = false }: { embedded?: boolean }) {
  const [data, setData] = useState<Intake>({ partner: [], expert: [], sprint: [] });
  const [tab, setTab] = useState<TabKey>('partner');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_BASE_URL}/auth/admin/intake`, { headers: { Authorization: `Bearer ${getToken() || ''}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || 'Failed to load submissions');
      setData({
        partner: (j.intake?.partner || []).filter(ownPartner),
        // Consulting-only intakes — never surfaced on the corporate admin.
        expert: IS_CORP ? [] : (j.intake?.expert || []),
        sprint: IS_CORP ? [] : (j.intake?.sprint || []),
      });
    } catch (e: any) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (type: TabKey, id: string, status: string) => {
    setData(d => ({ ...d, [type]: d[type].map(r => r.id === id ? { ...r, status } : r) })); // optimistic
    try {
      const r = await fetch(`${API_BASE_URL}/auth/admin/intake/${type}/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken() || ''}` }, body: JSON.stringify({ status }) });
      if (!r.ok) throw new Error();
    } catch { load(); } // revert by reloading on failure
  };

  const rows = data[tab];
  const cssVars = { ['--acc' as any]: ACCENT, ['--soft' as any]: IS_CORP ? '#FDE8CF' : '#DBEAFE' } as React.CSSProperties;

  /* Content is separated from the page chrome so AdminPartnersHub can render it
     inside its own shell as a tab, without a second sidebar and topbar. */
  const content = (
    <>
      <style>{CSS}</style>
      <div className={embedded ? '' : 'page'}>
          <div className="ph" style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <h1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Inbox className="w-5 h-5" style={{ color: ACCENT }} /> {IS_CORP ? 'Partner applications' : 'Applications & intakes'}</h1>
              <p>{IS_CORP
                ? 'Referral-partner applications submitted on connectcorporate.com. Expert and sprint intakes belong to the consulting brand and appear on conncct.com.'
                : 'Partner applications, expert-network applications and expert-sprint intakes from the marketing site.'}</p>
            </div>
            <button className="btn" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</button>
          </div>

          {/* One tab on corporate (partners only) — a switcher would be pointless. */}
          {TABS.length > 1 ? (
            <div className="tabs">
              {TABS.map(t => <button key={t.key} className={`tab ${tab === t.key ? 'on' : ''}`} onClick={() => setTab(t.key)}>{t.label}<span className="cnt">{data[t.key].length}</span></button>)}
            </div>
          ) : (
            <div style={{ margin: '20px 0 16px', fontSize: 12.5, color: '#64748B', fontWeight: 600 }}>
              {data.partner.length} application{data.partner.length === 1 ? '' : 's'}
            </div>
          )}

          {loading ? <div className="empty">Loading…</div>
            : error ? <div className="empty" style={{ color: '#DC2626', borderColor: '#FCDCDC' }}>{error}</div>
            : rows.length === 0 ? <div className="empty"><b>No {TABS.find(t => t.key === tab)?.label.toLowerCase()} yet</b><p style={{ marginTop: 4 }}>New submissions from the site appear here.</p></div>
            : rows.map(r => {
              const title = r.firm || r.name || r.cname || r.company || 'Submission';
              const initials = String(title).trim().split(/\s+/).slice(0, 2).map(x => x[0] || '').join('').toUpperCase() || '?';
              const entries = Object.entries(r).filter(([k, v]) => !HIDE.has(k) && v !== undefined);
              return (
                <div className="card" key={r.id}>
                  <div className="card-h">
                    <span className="av">{initials}</span>
                    <div style={{ minWidth: 0 }}>
                      <b>{title}</b>
                      <div className="em">{r.email || '—'}{r.name && r.firm ? ` · ${r.name}` : ''}</div>
                    </div>
                    <select className="pill" value={STATUSES.includes(r.status || '') ? r.status : 'new'} onChange={e => setStatus(tab, r.id, e.target.value)}>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  {entries.length > 0 && (
                    <div className="kv">{entries.map(([k, v]) => <div key={k}><span>{label(k)}</span><b>{val(v)}</b></div>)}</div>
                  )}
                  <div className="foot">Submitted {fmtDate(r.submittedAt)}{r.ref ? ` · Ref ${r.ref}` : ''}</div>
                </div>
              );
            })}
      </div>
    </>
  );

  if (embedded) return <div className="adminx" style={cssVars}>{content}</div>;

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <AdminSidebar />
      <div className="flex-1 xl:ml-64 overflow-x-hidden adminx" style={cssVars}>
        <header className="bg-white border-b border-[#E6E9ED] px-4 md:px-6 h-[58px] flex items-center justify-between sticky top-0 z-30">
          <span className="text-[13px] text-[#64748B] font-medium ml-10 xl:ml-0">Overview / <b className="text-[#0F172A] font-semibold">{IS_CORP ? 'Partners' : 'Applications'}</b></span>
          <AdminProfileMenu />
        </header>
        {content}
      </div>
    </div>
  );
}
